import Spinner from "components/Spinner";
import React, { useEffect, useRef, useState } from "react";
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  customClasses,
  width,
  searchFunction,
  RenderItem,
  ResetComponent,
  selectedLabel,
  getOptionLabel,
  // Text already typed in the search when it mounts, e.g. the shipment's
  // company name on Shipments Update Customer (user, 2026-10-30).
  defaultSearch,
}) {
  /* ******************** All States ************************* */
  const [isFocussed, setIsFocussed] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState(options || []);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(null);
  const [totalPages, setTotalPages] = useState(null);

  const [loading, setLoading] = useState(false);

  // Refs
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);

  const getLabel =
    getOptionLabel || ((option) => (option != null ? String(option) : ""));

  /* ******************** All Functions ************************* */
  const closeDropdown = () => {
    setIsFocussed(false);
    setSearchText("");
    setPage(1);
  };

  /* ******************** All UseEffects ************************* */
  // Effect to handle search functionality
  useEffect(() => {
    let tid = setTimeout(() => {
      if (searchText && searchFunction) {
        const currentId = ++requestIdRef.current;
        setLoading(true);
        searchFunction(searchText, page, limit)
          .then((res) => {
            if (currentId !== requestIdRef.current) return;

            let results = [];
            let totalValue = null;
            let totalPagesValue = null;

            if (Array.isArray(res)) {
              results = res;
              totalValue = res.length;
            } else if (res && typeof res === "object") {
              results = Array.isArray(res.results) ? res.results : [];
              totalValue =
                typeof res.total === "number" ? res.total : res.total || null;
              totalPagesValue =
                typeof res.totalPages === "number"
                  ? res.totalPages
                  : res.totalPages || null;
            }

            if (page > 1) {
              setSearchResults((prevResults) => [
                ...prevResults,
                ...(results || []),
              ]);
            } else {
              setSearchResults(results || []);
            }
            setTotal(totalValue);
            setTotalPages(totalPagesValue);
            setLoading(false);
          })
          .catch(() => {
            if (currentId !== requestIdRef.current) return;
            setLoading(false);
          });
      } else if (searchText) {
        // Default search functionality: filter options based on searchText
        const baseOptions = options || [];
        const lowerSearch = searchText.toLowerCase();
        const results = baseOptions.filter((option) =>
          getLabel(option).toLowerCase().includes(lowerSearch),
        );
        setSearchResults(results);
      }
    }, 500); // Debounce time of 1 second
    return () => clearTimeout(tid);
  }, [searchText, searchFunction, options, page, limit]);

  // keep search results in sync with local options when not using remote search
  useEffect(() => {
    if (!searchFunction && !searchText) {
      setSearchResults(options || []);
      setTotal(options ? options.length : null);
    }
  }, [options, searchFunction, searchText]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      // run search function with empty string to load initial options
      if (searchFunction) {
        const currentId = ++requestIdRef.current;
        setPage(1);
        setLoading(true);
        searchFunction("", 1, limit)
          .then((res) => {
            if (currentId !== requestIdRef.current) return;

            let results = [];
            let totalValue = null;
            let totalPagesValue = null;

            if (Array.isArray(res)) {
              results = res;
              totalValue = res.length;
            } else if (res && typeof res === "object") {
              results = Array.isArray(res.results) ? res.results : [];
              totalValue =
                typeof res.total === "number" ? res.total : res.total || null;
              totalPagesValue =
                typeof res.totalPages === "number"
                  ? res.totalPages
                  : res.totalPages || null;
            }

            setSearchResults(results || []);
            setTotal(totalValue);
            setTotalPages(totalPagesValue);
            setLoading(false);
          })
          .catch(() => {
            if (currentId !== requestIdRef.current) return;
            setLoading(false);
          });
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [searchFunction, limit]);

  // Effect to close the container when clicked outside
  useEffect(() => {
    function closeContainerOnClickOutside(e) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        !inputRef.current.contains(e.target)
      ) {
        setIsFocussed(false);
      }
    }
    if (isFocussed) {
      // Add event listener to detect clicks outside the container
      window.addEventListener("click", closeContainerOnClickOutside);
    } else {
      window.removeEventListener("click", closeContainerOnClickOutside);
    }
  }, [containerRef, isFocussed, inputRef]);

  useEffect(() => {
    if (!value) {
      setSearchText("");
    }
  }, [value]);

  // After the clear above, so it is not wiped on mount.
  useEffect(() => {
    if (defaultSearch) {
      setPage(1);
      setSearchText(defaultSearch);
    }
  }, [defaultSearch]);

  return (
    <>
      <div className="position-relative">
        <input
          type="text"
          ref={inputRef}
          value={
            isFocussed ? searchText : selectedLabel || value || searchText || ""
          }
          onChange={(e) => {
            setPage(1);
            const newValue = e.target.value;
            setSearchText(newValue);
            if (onChange) {
              onChange(newValue);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setIsFocussed(false);
              e.stopPropagation();
            }

            if (e.key === "Tab") {
              // Close dropdown but allow focus to move to the next field
              setIsFocussed(false);
            }
          }}
          placeholder={placeholder || "Select..."}
          className={`form-control ${customClasses}`}
          style={width && { width }}
          onFocus={() => setIsFocussed(true)}
        />
        <div>
          {isFocussed && (
            <div
              ref={containerRef}
              className="position-absolute bg-white border mt-1 shadow-lg"
              style={{
                zIndex: 1000,
                width: width || "100%",
                maxWidth: width || "100%",
                minHeight: "100px",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <div className="positidon-relative">
                {loading && <Spinner />}
                {/* Stats */}
                <div className="d-flex justify-content-between align-items-center">
                  {ResetComponent && (
                    <>
                      {typeof ResetComponent === "function" ? (
                        <ResetComponent close={closeDropdown} />
                      ) : (
                        ResetComponent
                      )}
                    </>
                  )}
                  {searchFunction ? (
                    <div className="p-1 px-2">
                      Showing {searchResults.length} result
                      {searchResults.length !== 1 ? "s" : ""} of{" "}
                      {total || "all"}
                    </div>
                  ) : (
                    options && (
                      <div className="p-1 px-2">
                        Showing {searchResults.length} result
                        {searchResults.length !== 1 ? "s" : ""} of{" "}
                        {options.length}
                      </div>
                    )
                  )}
                </div>
                <hr className="mt-0 mb-1" />

                {searchResults.length > 0 ? (
                  searchResults.map((item, index) => (
                    <RenderItem key={index} item={item} index={index} />
                  ))
                ) : (
                  <div className="p-2">No results found</div>
                )}
                {/* if current showing is less than total, show load more button */}
                {searchResults.length < total && !loading && (
                  <div className="p-2">
                    <button
                      className="btn btn-sm btn-primary w-100"
                      onClick={() => {
                        setPage(page + 1);
                      }}
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
